// Round 28-AC: public self-service registration page.
//
// Two steps:
//   1. Enter email + display name (+ optional phone). We email a 6-digit code.
//   2. Enter the code. We create the account and email a welcome with QR
//      + recovery codes + addin download link.
//
// Domain-restricted to @broadnet.me / @broadnetme.com — server enforces;
// the form just hints up-front so the user doesn't waste time typing.

import {
  Alert, Button, Card, Center, Container, Group, Loader,
  PasswordInput, Stack, Stepper, Text, TextInput, Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../api.js';

const ALLOWED_DOMAINS = ['broadnet.me', 'broadnetme.com'] as const;

function isAllowedEmail(email: string): boolean {
  const at = email.lastIndexOf('@');
  if (at < 1) return false;
  const d = email.slice(at + 1).toLowerCase();
  return (ALLOWED_DOMAINS as readonly string[]).includes(d);
}

export function RegisterPage(): React.JSX.Element {
  const [active, setActive] = useState<number>(0);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<{
    email: string; sign_in_url: string; addin_download_url: string; email_sent: boolean;
  } | null>(null);

  const emailLooksOk = email.includes('@') && isAllowedEmail(email);

  async function submitStep1() {
    setErr(null);
    if (!emailLooksOk) {
      setErr('Email must end in @broadnet.me or @broadnetme.com.');
      return;
    }
    if (displayName.trim().length < 2) {
      setErr('Enter your full name.');
      return;
    }
    if (phone && !/^\+\d{8,15}$/.test(phone)) {
      setErr('Phone must be in E.164 format, e.g. +9613123456.');
      return;
    }
    setBusy(true);
    try {
      await api.authRegisterStart({
        email: email.trim().toLowerCase(),
        display_name: displayName.trim(),
        ...(phone ? { phone_e164: phone.trim() } : {}),
      });
      setActive(1);
    } catch (e) {
      const apiErr = e as ApiError;
      // Surface server-side messages where helpful.
      if (apiErr?.status === 403) {
        setErr('Registration is restricted to @broadnet.me and @broadnetme.com email addresses.');
      } else if (apiErr?.status === 409) {
        setErr('This email is already registered. Try signing in instead.');
      } else if (apiErr?.status === 429) {
        setErr('Too many attempts from this network. Try again in an hour.');
      } else if (apiErr?.status === 503) {
        setErr('We couldn\'t send the verification email right now. Try again in a minute.');
      } else {
        setErr(e instanceof Error ? e.message : 'Could not start registration.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitStep2() {
    setErr(null);
    if (!/^\d{6}$/.test(code.trim())) {
      setErr('Enter the 6-digit code from the email.');
      return;
    }
    setBusy(true);
    try {
      const r = await api.authRegisterVerify({
        email: email.trim().toLowerCase(),
        code: code.trim(),
      });
      setDone({
        email: r.email,
        sign_in_url: r.sign_in_url,
        addin_download_url: r.addin_download_url,
        email_sent: r.email_sent,
      });
      setActive(2);
      if (!r.email_sent) {
        notifications.show({
          color: 'yellow',
          title: 'Account created — but welcome email failed',
          message: 'Contact your admin to receive credentials.',
          autoClose: 12_000,
        });
      }
    } catch (e) {
      const apiErr = e as ApiError;
      if (apiErr?.status === 401) {
        setErr('Wrong code. Check the email we sent and try again.');
      } else if (apiErr?.status === 410) {
        setErr('Code expired (codes are valid for 10 minutes). Start over to get a fresh one.');
      } else if (apiErr?.status === 429) {
        setErr('Too many wrong codes. Start over to get a fresh one.');
      } else if (apiErr?.status === 404) {
        setErr('No active code for this email. Start over.');
      } else {
        setErr(e instanceof Error ? e.message : 'Could not verify the code.');
      }
    } finally {
      setBusy(false);
    }
  }

  function startOver() {
    setActive(0);
    setCode('');
    setDone(null);
    setErr(null);
  }

  return (
    <Container size="sm" py="xl">
      <Stack gap="md">
        <Stack gap={2} align="center" mb="md">
          <Title order={2}>Join AI Outlook</Title>
          <Text size="sm" c="dimmed">For BroadNet team members only</Text>
        </Stack>

        <Card padding="lg" withBorder radius="md">
          <Stepper active={active}>
            <Stepper.Step label="Email" description="Verify your address">
              <Stack gap="md" mt="md">
                <TextInput
                  label="Work email"
                  placeholder="rabih@broadnet.me"
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  required
                  type="email"
                  description="Must end in @broadnet.me or @broadnetme.com"
                  error={email.length > 0 && !emailLooksOk ? 'Domain not allowed' : null}
                />
                <TextInput
                  label="Full name"
                  placeholder="Rabih Bzeih"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.currentTarget.value)}
                  required
                />
                <TextInput
                  label="Phone (optional)"
                  placeholder="+9611234567"
                  value={phone}
                  onChange={(e) => setPhone(e.currentTarget.value)}
                  description="E.164 format, used for SMS 2FA fallback"
                />
                {err && <Alert color="red" variant="light">{err}</Alert>}
                <Group justify="space-between">
                  <Button variant="subtle" component={Link} to="/">Back to sign in</Button>
                  <Button onClick={submitStep1} loading={busy}>Send verification code</Button>
                </Group>
              </Stack>
            </Stepper.Step>

            <Stepper.Step label="Code" description="Check your inbox">
              <Stack gap="md" mt="md">
                <Alert color="blue" variant="light" title="Check your email">
                  We sent a 6-digit code to <b>{email}</b>. It expires in 10 minutes.
                </Alert>
                <PasswordInput
                  label="6-digit verification code"
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.currentTarget.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  visible
                  styles={{ input: { fontFamily: 'monospace', fontSize: 18, letterSpacing: 4, textAlign: 'center' } }}
                />
                {err && <Alert color="red" variant="light">{err}</Alert>}
                <Group justify="space-between">
                  <Button variant="subtle" onClick={startOver}>Start over</Button>
                  <Button onClick={submitStep2} loading={busy}>Verify & create account</Button>
                </Group>
                <Text size="xs" c="dimmed" ta="center">
                  Can't find the email? Check spam, or <Link to="/" onClick={() => { startOver(); setTimeout(() => submitStep1(), 100); }} style={{ color: 'inherit', textDecoration: 'underline' }}>start over</Link>.
                </Text>
              </Stack>
            </Stepper.Step>

            <Stepper.Completed>
              <Stack gap="md" mt="md" align="center">
                <Center>
                  <div style={{ fontSize: 48 }}>✓</div>
                </Center>
                <Title order={3} ta="center">Welcome to AI Outlook!</Title>
                {done?.email_sent ? (
                  <Alert color="green" variant="light" title="Check your inbox">
                    We sent everything you need to <b>{done.email}</b>:
                    <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
                      <li>Your temporary password</li>
                      <li>QR code for Google Authenticator (or any TOTP app)</li>
                      <li>10 single-use recovery codes — save these somewhere safe</li>
                      <li>Outlook addin download link + install steps</li>
                    </ul>
                  </Alert>
                ) : (
                  <Alert color="yellow" variant="light" title="Account created">
                    We couldn't send your welcome email. Contact your admin to receive your credentials.
                  </Alert>
                )}
                <Group>
                  <Button component={Link} to="/get-started">Set up the addin</Button>
                  <Button variant="default" component={Link} to="/">Sign in</Button>
                </Group>
              </Stack>
            </Stepper.Completed>
          </Stepper>
        </Card>

        {active < 2 && (
          <Text size="xs" c="dimmed" ta="center">
            By creating an account you agree to use AI Outlook only for your own BroadNet email. Each user sees only their own mailbox — there is no cross-user access.
          </Text>
        )}
      </Stack>
    </Container>
  );
}
